import {HttpStatusCode} from "@angular/common/http";
import {inject, Service} from "@angular/core";
import type {IJsonObject} from "dcerialize";
import {endWith, type Observable, switchMap} from "rxjs";
import {WEB_APP_CONFIG} from "../../../common/app-config";
import {RequestBuilder} from "../../models/RequestBuilder";
import {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  ResetPasswordRequest,
  TokenResponse,
} from "../../models/users/Login";
import {CredentialsStorage} from "../credentials-storage";
import {TableParametersStorage} from "../table-parameters-storage";
import {BaseApi} from "./base-api";

@Service()
export class AuthApi extends BaseApi {
  private readonly credentialsStorage = inject(CredentialsStorage);
  private readonly tableParametersStorage = inject(TableParametersStorage);
  private readonly webAppConfig = inject(WEB_APP_CONFIG);

  public constructor() {
    super("auth");
  }

  // eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
  private executeTokenRequest<TBody>(request: RequestBuilder<TBody, IJsonObject>): Observable<TokenResponse> {
    return this.execute(request).notNull().decodeJsonObjectOf(TokenResponse)
      .pipe(switchMap(res => this.credentialsStorage.setCredentialsFrom(res).pipe(endWith(res))));
  }

  public login(email: string, password: string,
               ignoreFailureStatusCodes?: readonly HttpStatusCode[]): Observable<TokenResponse> {
    const request = RequestBuilder.post(
      new LoginRequest(this.webAppConfig.apiTokenAudience, email, password), "login")
      .serializeAsJsonOf(LoginRequest).expectJsonObject()
      .skipErrorHandlerForStatusCodes(...(ignoreFailureStatusCodes ?? []));
    return this.executeTokenRequest(request);
  }

  public logout(): Observable<never> {
    this.tableParametersStorage.clearAllTableParameters();
    return this.credentialsStorage.clearCredentials();
  }

  public forgotPassword(email: string, returnUrl?: string): Observable<never> {
    const request = RequestBuilder.post(new ForgotPasswordRequest(email, returnUrl), "forgot-password")
      .serializeAsJsonOf(ForgotPasswordRequest).expectNoContent();
    return this.execute(request)
      .statusCodeIn(HttpStatusCode.Accepted, HttpStatusCode.NoContent)
      .noContent(false);
  }

  public changePassword(oldPassword: string, newPassword: string,
                        ignoreFailureStatusCodes?: readonly HttpStatusCode[]): Observable<TokenResponse> {
    const request = RequestBuilder.post(new ChangePasswordRequest(oldPassword, newPassword), "change-password")
      .serializeAsJsonOf(ChangePasswordRequest).authenticate().expectJsonObject()
      .skipErrorHandlerForStatusCodes(...(ignoreFailureStatusCodes ?? []));
    return this.executeTokenRequest(request);
  }

  public resetPassword(email: string, resetToken: string, newPassword: string): Observable<never> {
    const request = RequestBuilder.post(new ResetPasswordRequest(email, resetToken, newPassword), "reset-password")
      .serializeAsJsonOf(ResetPasswordRequest).expectNoContent();
    return this.execute(request).noContent();
  }
}
